/* MolCellQuest 問題データ — シグナル伝達・発生・がん
   ★ 配列に追記すればそのまま反映されます（id は全体で一意に）。
   ★ diff : 80〜320。基礎 0-179 / 標準 180-269 / 応用 270+ */

const CAT = { track: "molecule", category: "signaling", categoryName: "シグナル伝達・発生・がん" };

export const MOLECULE_SIGNALING = [
  {
    id: "sig01", ...CAT,
    title: "受容体の場所とリガンドの性質", diff: 110, tags: ["受容体", "ステロイドホルモン", "細胞膜"],
    explain: `<p>ステロイドホルモンの受容体が<b>細胞内（細胞質や核）</b>にあるのはなぜか。
              最も適切な理由はどれか。</p>`,
    type: "radio",
    options: [
      "ステロイドは分子量が大きすぎて細胞膜を通れないため。",
      "ステロイドは脂溶性で細胞膜を通過できるため、受容体が細胞内にあってよい。",
      "ステロイドは水溶性で、能動輸送によって細胞内へ運ばれるため。",
      "ステロイドは細胞内でのみ合成されるため。",
    ],
    answer: 1,
    hints: [
      "ステロイドはコレステロールから作られます。水になじむでしょうか、脂質になじむでしょうか。",
      "膜を自由に通れるなら、受容体を膜の表面に置く必要はありません。",
    ],
    solution: `<p><b>【ステップ1】リガンドの性質が受容体の場所を決める</b></p>
               <table>
                 <tr><th>リガンド</th><th>膜透過</th><th>受容体の場所</th><th>応答の速さ</th></tr>
                 <tr><td>ペプチドホルモン、成長因子<br>（水溶性）</td><td>できない</td><td><b>細胞膜表面</b></td><td>秒〜分</td></tr>
                 <tr><td>ステロイド、甲状腺ホルモン<br>（脂溶性）</td><td><b>できる</b></td><td><b>細胞質・核</b></td><td>時間〜日</td></tr>
               </table>
               <p><b>【ステップ2】ステロイド受容体の働き方</b></p>
               <ol>
                 <li>脂溶性のステロイドが細胞膜を<b>単純拡散で</b>通過</li>
                 <li>細胞質または核内の受容体に結合</li>
                 <li>シャペロン（Hsp90）が外れ、二量体化して核へ</li>
                 <li>DNA上のホルモン応答配列に結合し、<b>転写因子として</b>働く</li>
               </ol>
               <p><b>【ステップ3】応答の速さの違いが生まれる理由</b></p>
               <p>ステロイドの作用は<b>遺伝子発現の変化</b>を介するため、
               転写 → 翻訳 → タンパク質蓄積という段階を踏み、
               効果が出るまで数時間かかります。
               その代わり<b>持続時間が長い</b>。</p>
               <p>一方アドレナリンのような膜受容体を介するシグナルは、
               既にある酵素をリン酸化するだけなので<b>秒単位</b>で応答できます。</p>
               <p>「速いが短い」細胞膜受容体と、「遅いが長い」核内受容体。
               生体はこの2種類を使い分けています。</p>
               <p class="tiny">A が誤りである点にも注意。ステロイドはむしろ分子量が小さく（約300）、
               通れない理由は大きさではありません。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "sig02", ...CAT,
    title: "セカンドメッセンジャー", diff: 130, tags: ["cAMP", "Ca2+", "IP3"],
    explain: `<p>セカンドメッセンジャーに<b>当てはまらない</b>ものはどれか。</p>`,
    type: "radio",
    options: ["cAMP", "IP₃（イノシトール三リン酸）", "Ca²⁺", "インスリン"],
    answer: 3,
    hints: [
      "セカンドメッセンジャーは「細胞内で」情報を広げる小分子です。",
      "インスリンは細胞の外から来る「ファーストメッセンジャー」です。",
    ],
    solution: `<p><b>【ステップ1】用語を整理する</b></p>
               <ul>
                 <li><b>ファーストメッセンジャー</b>：細胞外のシグナル分子。
                     ホルモン、神経伝達物質、成長因子（インスリン、アドレナリンなど）</li>
                 <li><b>セカンドメッセンジャー</b>：受容体の活性化を受けて
                     <b>細胞内で</b>急激に濃度が変わる小分子。シグナルを増幅・拡散する</li>
               </ul>
               <p><b>【ステップ2】主なセカンドメッセンジャー</b></p>
               <table>
                 <tr><th>分子</th><th>作る酵素</th><th>主な標的</th></tr>
                 <tr><td>cAMP</td><td>アデニル酸シクラーゼ</td><td>PKA</td></tr>
                 <tr><td>cGMP</td><td>グアニル酸シクラーゼ</td><td>PKG</td></tr>
                 <tr><td>IP₃</td><td>ホスホリパーゼC</td><td>小胞体の IP₃受容体 → Ca²⁺ 放出</td></tr>
                 <tr><td>DAG</td><td>ホスホリパーゼC</td><td>PKC</td></tr>
                 <tr><td>Ca²⁺</td><td>（チャネルから流入）</td><td>カルモジュリン</td></tr>
               </table>
               <p><b>【ステップ3】増幅という機能</b></p>
               <p>セカンドメッセンジャーの本質は<b>増幅</b>です。
               1分子のアドレナリンが受容体に結合すると、
               1個の受容体が複数のGタンパク質を活性化し、
               1個のアデニル酸シクラーゼが多数の cAMP を作り、
               1個の PKA が多数の基質をリン酸化する。</p>
               <p>この段階的な増幅により、<b>ごく微量のホルモンが
               細胞全体の応答を引き起こせます</b>。
               肝細胞のグリコーゲン分解では、
               10⁻¹⁰ M のアドレナリンが
               10⁻³ M のグルコース放出につながる——約10⁷倍の増幅です。</p>
               <p>したがって、当てはまらないのは <b>インスリン (D)</b> です。</p>`,
  },

  {
    id: "sig03", ...CAT,
    title: "Gタンパク質共役受容体", diff: 170, tags: ["GPCR", "三量体Gタンパク質", "GTP"],
    explain: `<p>Gタンパク質共役受容体（GPCR）のシグナル伝達について、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "リガンドが結合すると、Gαサブユニットに結合していたGTPがGDPに交換される。",
      "リガンドが結合すると、GαのGDPがGTPに交換され、GαがGβγから解離して下流を活性化する。",
      "Gαは自身にGTP分解活性を持たないため、一度活性化すると永久に活性のままである。",
      "GPCRは1回膜貫通型の受容体である。",
    ],
    answer: 1,
    hints: [
      "Gタンパク質は「GTP結合型が活性、GDP結合型が不活性」というスイッチです。",
      "コレラ毒素は Gαs の GTP分解活性を失わせます。それが症状を引き起こすということは、通常は分解されるということです。",
    ],
    solution: `<p><b>【ステップ1】GPCR の基本構造</b></p>
               <p><b>7回膜貫通型</b>の受容体で（D は誤り）、
               ヒトゲノムに約800種類あり、承認薬の約3割が標的とする最大の創薬標的群です。</p>
               <p><b>【ステップ2】活性化サイクル</b></p>
               <ol>
                 <li>静止時：Gα に <b>GDP</b> が結合、Gαβγ の三量体を形成（不活性）</li>
                 <li>リガンド結合 → 受容体が構造変化し、<b>GEF（グアニンヌクレオチド交換因子）</b>として働く</li>
                 <li>Gα の <b>GDP → GTP</b> 交換が起こる</li>
                 <li>Gα-GTP が Gβγ から解離し、<b>両方が</b>下流を制御する</li>
                 <li>Gα 自身の <b>GTPアーゼ活性</b>で GTP → GDP に加水分解</li>
                 <li>再び三量体に戻る（不活性化）</li>
               </ol>
               <p><b>【ステップ3】自己不活性化がなぜ重要か</b></p>
               <p>Gα は<b>自分でタイマーを持っている</b>点が巧妙です。
               GTP を分解すれば自動的にオフに戻るので、
               シグナルは自然に終息します。</p>
               <p>この仕組みが壊れると重大な結果を招きます。</p>
               <ul>
                 <li><b>コレラ毒素</b>：Gαs を ADP-リボシル化して GTPアーゼ活性を失わせる →
                     Gαs が活性化しっぱなし → cAMP が持続的に上昇 →
                     腸管上皮から Cl⁻ と水が大量に流出 → <b>激しい水様性下痢</b></li>
                 <li><b>百日咳毒素</b>：Gαi を修飾して受容体と共役できなくする →
                     抑制がかからず cAMP が上昇</li>
               </ul>
               <p>「オフにできない」だけで致死的な病気になる——
               シグナルの終息が伝達と同じくらい重要であることの証拠です。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "sig04", ...CAT,
    title: "受容体チロシンキナーゼとMAPK経路", diff: 220, tags: ["RTK", "Ras", "MAPK"],
    explain: `<p>受容体チロシンキナーゼ（RTK）から Ras-MAPK 経路への伝達の順序として、
              正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "リガンド結合 → 受容体の二量体化と自己リン酸化 → Grb2/SOS の結合 → Ras の活性化 → Raf → MEK → ERK",
      "リガンド結合 → Ras の活性化 → 受容体の二量体化 → ERK → MEK → Raf",
      "リガンド結合 → cAMP 産生 → PKA → Ras → ERK",
      "リガンド結合 → 受容体が核へ移行 → 直接転写を活性化",
    ],
    answer: 0,
    hints: [
      "RTK は自分自身をリン酸化することでアダプタータンパク質の足場を作ります。",
      "MAPK カスケードは Raf（MAPKKK）→ MEK（MAPKK）→ ERK（MAPK）の3段階です。",
    ],
    solution: `<p><b>【ステップ1】RTK の活性化</b></p>
               <ol>
                 <li>リガンド（EGF など）が結合</li>
                 <li>受容体が<b>二量体化</b>する</li>
                 <li>互いの細胞内領域のチロシンを<b>トランスに自己リン酸化</b></li>
                 <li>リン酸化チロシンが <b>SH2ドメイン</b>を持つタンパク質の結合部位になる</li>
               </ol>
               <p>ここが要点です。受容体は自らリン酸化することで
               <b>「ドッキングステーション」に変身</b>します。</p>
               <p><b>【ステップ2】Ras の活性化</b></p>
               <ol start="5">
                 <li><b>Grb2</b>（アダプター）が SH2 でリン酸化チロシンに結合</li>
                 <li>Grb2 が <b>SOS</b>（GEF）を連れてくる</li>
                 <li>SOS が Ras の <b>GDP → GTP</b> 交換を促す</li>
                 <li>Ras-GTP（活性型）が Raf を膜へ引き寄せて活性化</li>
               </ol>
               <p><b>【ステップ3】MAPK カスケード</b></p>
               <p><b>Raf（MAPKKK）→ MEK（MAPKK）→ ERK（MAPK）</b>という
               3段階のリン酸化リレーです。ERK は核へ移行し、
               転写因子をリン酸化して細胞周期関連遺伝子（<i>MYC</i>, <i>CCND1</i>）を誘導します。</p>
               <p><b>【ステップ4】なぜ3段も重ねるのか</b></p>
               <ul>
                 <li><b>増幅</b>：各段で複数の基質をリン酸化できる</li>
                 <li><b>スイッチ的応答</b>：多段の協同性により、
                     入力に対して<b>デジタル的な全か無かの応答</b>を作れる</li>
                 <li><b>調節点の増加</b>：各段にフィードバックや足場タンパク質を配置できる</li>
               </ul>
               <p class="tiny">この経路の各段階ががんで変異します。
               EGFR（肺がん）、KRAS（膵臓がん・大腸がん）、BRAF（悪性黒色腫）。
               それぞれに分子標的薬（ゲフィチニブ、ベムラフェニブなど）が開発されています。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "sig05", ...CAT,
    title: "がん遺伝子とがん抑制遺伝子", diff: 200, tags: ["がん遺伝子", "がん抑制遺伝子", "two-hit"],
    explain: `<p>がん遺伝子（オンコジーン）とがん抑制遺伝子の性質の違いとして、正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "がん遺伝子は機能獲得型変異で優性に働き、がん抑制遺伝子は機能喪失型変異で通常は両アレルの不活性化を要する。",
      "がん遺伝子は機能喪失型変異で劣性に働き、がん抑制遺伝子は機能獲得型で優性に働く。",
      "どちらも片方のアレルの変異だけでがん化する。",
      "どちらも両方のアレルが失われないとがん化しない。",
    ],
    answer: 0,
    hints: [
      "アクセルとブレーキに例えると分かりやすい。アクセルは片方が踏みっぱなしでも進みますが、ブレーキは？",
      "クヌードソンの two-hit 仮説はどちらの話でしょうか。",
    ],
    solution: `<p><b>【ステップ1】アクセルとブレーキ</b></p>
               <table>
                 <tr><th></th><th>がん遺伝子</th><th>がん抑制遺伝子</th></tr>
                 <tr><td>たとえ</td><td><b>アクセル</b></td><td><b>ブレーキ</b></td></tr>
                 <tr><td>正常時の役割</td><td>増殖の促進</td><td>増殖の抑制、修復、アポトーシス</td></tr>
                 <tr><td>変異の型</td><td><b>機能獲得</b>（gain of function）</td><td><b>機能喪失</b>（loss of function）</td></tr>
                 <tr><td>必要な変異</td><td><b>片アレルで十分</b>（優性）</td><td><b>両アレル</b>（劣性）</td></tr>
                 <tr><td>例</td><td>RAS, MYC, EGFR, BCR-ABL</td><td>TP53, RB1, BRCA1/2, APC</td></tr>
               </table>
               <p>アクセルは片方が踏みっぱなしになれば車は進みます。
               ブレーキは片方が生きていれば止まれるので、
               両方を失わないと暴走しません。</p>
               <p><b>【ステップ2】クヌードソンの two-hit 仮説</b></p>
               <p>網膜芽細胞腫の疫学から導かれた説です。</p>
               <ul>
                 <li><b>遺伝性</b>：生まれつき片アレルに変異（1st hit）を持つ。
                     体細胞で残る1つに変異が起これば発症 → <b>若年・両眼性・多発性</b></li>
                 <li><b>孤発性</b>：同じ細胞で2回の変異が独立に起こる必要がある →
                     <b>高齢・片眼性・単発</b></li>
               </ul>
               <p>この統計的な発症パターンの違いから、
               <i>RB1</i> というがん抑制遺伝子の存在が
               <b>遺伝子が同定される前に</b>予言されました。</p>
               <p><b>【ステップ3】例外にも触れておく</b></p>
               <p>「両アレル必要」には例外があります。
               <b>ハプロ不全</b>（1コピーでは量が足りない）や、
               p53 の<b>ドミナントネガティブ変異</b>
               （変異型が正常型と四量体を作って機能を妨げる）では、
               片アレルの変異でも影響が出ます。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "sig06", ...CAT,
    title: "モルフォゲンと位置情報", diff: 245, tags: ["モルフォゲン", "濃度勾配", "発生"],
    explain: `<p>発生におけるモルフォゲンについて、最も適切な説明はどれか。</p>`,
    type: "radio",
    options: [
      "細胞間を移動せず、産生した細胞自身にのみ作用する分子である。",
      "濃度勾配を形成し、その濃度の違いに応じて細胞が異なる運命をとるよう指示する分子である。",
      "すべての細胞に同じ濃度で作用し、一斉に同じ分化を誘導する分子である。",
      "細胞の移動速度だけを制御し、分化には関与しない分子である。",
    ],
    answer: 1,
    hints: [
      "「フランスの国旗モデル」を思い出しましょう。1つの勾配から3色の領域ができます。",
      "同じ分子でも、濃度が違えば違う遺伝子が発現します。",
    ],
    solution: `<p><b>【ステップ1】モルフォゲンの定義</b></p>
               <p>特定の場所（源）から拡散して<b>濃度勾配</b>を作り、
               細胞がその<b>濃度を読み取って</b>位置を知り、
               それに応じた運命を選ぶような分子です。</p>
               <p><b>【ステップ2】フランス国旗モデル</b></p>
               <p>ウォルパートの比喩です。一列に並んだ細胞に濃度勾配がかかり、
               細胞は2つの閾値を持つとします。</p>
               <table>
                 <tr><th>濃度</th><th>運命</th></tr>
                 <tr><td>高い（閾値2以上）</td><td>青</td></tr>
                 <tr><td>中間（閾値1〜2）</td><td>白</td></tr>
                 <tr><td>低い（閾値1未満）</td><td>赤</td></tr>
               </table>
               <p><b>連続的な</b>濃度勾配から、<b>離散的な</b>3つの領域ができる。
               1種類の分子で複数の運命を指定できる、という点が本質です。</p>
               <p><b>【ステップ3】実例</b></p>
               <ul>
                 <li><b>ビコイド</b>（ショウジョウバエ）：母性 mRNA が卵の前極に局在し、
                     翻訳されたタンパク質が濃度勾配を作って前後軸を決める。
                     最初に同定されたモルフォゲン。</li>
                 <li><b>Shh（ソニック・ヘッジホッグ）</b>：脊索と底板から分泌され、
                     神経管の腹側で高濃度。濃度に応じて異なる神経細胞
                     （運動ニューロン、各種介在ニューロン）を作り分ける。</li>
                 <li><b>レチノイン酸</b>：Hox 遺伝子の発現域を制御し、前後軸の位置情報を与える。</li>
               </ul>
               <p><b>【ステップ4】臨床との接続</b></p>
               <p>Shh 経路の異常は全前脳胞症（脳が左右に分かれない）を起こします。
               また妊娠初期のレチノイン酸（ビタミンA誘導体）の過剰摂取が
               重い催奇形性を持つのは、この位置情報系を撹乱するためです。
               ニキビ治療薬イソトレチノインが妊婦に厳禁なのはこの理由によります。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "sig07", ...CAT,
    title: "シグナルの終息機構", diff: 235, tags: ["脱感作", "ホスファターゼ", "負のフィードバック"],
    explain: `<p>細胞がシグナルを受け続けたとき、応答を弱める（脱感作する）機構として
              <b>当てはまらない</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "受容体のリン酸化とβアレスチンの結合による、Gタンパク質との共役の遮断",
      "受容体のエンドサイトーシスによる細胞表面からの除去",
      "ホスホジエステラーゼによる cAMP の分解",
      "受容体の発現量を増やして、より多くのリガンドを結合させること",
    ],
    answer: 3,
    hints: [
      "脱感作は「応答を弱める」方向の調節です。受容体を増やすとどうなるでしょう。",
      "受容体を増やすのはむしろ感受性を高める（up-regulation）方向です。",
    ],
    solution: `<p><b>【ステップ1】終息が必要な理由</b></p>
               <p>シグナルは<b>始めることと同じくらい、終えることが重要</b>です。
               止められないシグナルは病気を引き起こします
               （コレラ毒素、がん遺伝子の恒常活性化）。
               また、常に同じ強さで応答していては
               <b>変化を検出できません</b>。</p>
               <p><b>【ステップ2】主な終息機構</b></p>
               <table>
                 <tr><th>層</th><th>機構</th><th>時間スケール</th></tr>
                 <tr><td>受容体</td><td>GRK によるリン酸化 → <b>βアレスチン</b>結合で共役遮断</td><td>秒〜分</td></tr>
                 <tr><td>受容体</td><td><b>エンドサイトーシス</b>で表面から除去（分解またはリサイクル）</td><td>分</td></tr>
                 <tr><td>Gタンパク質</td><td>Gα の GTPアーゼ活性、RGS による促進</td><td>秒</td></tr>
                 <tr><td>二次伝達物質</td><td><b>ホスホジエステラーゼ</b>による cAMP/cGMP 分解</td><td>秒</td></tr>
                 <tr><td>リン酸化</td><td>ホスファターゼによる脱リン酸化</td><td>秒〜分</td></tr>
                 <tr><td>転写</td><td>負のフィードバック因子（SOCS, MKP など）の誘導</td><td>時間</td></tr>
               </table>
               <p><b>【ステップ3】D が方向として逆である理由</b></p>
               <p>受容体を増やす（アップレギュレーション）のは
               <b>感受性を高める</b>調節で、脱感作の逆です。
               慢性的な刺激では通常、受容体は<b>減少</b>します
               （ダウンレギュレーション）。</p>
               <p><b>【ステップ4】臨床でよく見る帰結</b></p>
               <ul>
                 <li><b>薬剤耐性</b>：βアゴニストを吸入し続けると、
                     β受容体がダウンレギュレートして効きが悪くなる</li>
                 <li><b>離脱症状</b>：オピオイド受容体が脱感作した状態で
                     薬を止めると、内因性リガンドでは足りず症状が出る</li>
                 <li><b>順応</b>：明るい場所に出た直後は眩しいが、
                     やがて慣れる（視細胞の脱感作）</li>
               </ul>
               <p class="tiny">薬理学的に興味深いのは、
               ホスホジエステラーゼ阻害剤が「終息を止める」薬だという点です。
               シルデナフィル（PDE5阻害）は cGMP の分解を妨げ、
               シグナルを持続させることで血管を拡張させます。</p>
               <p>したがって、当てはまらないのは <b>D</b> です。</p>`,
  },

  {
    id: "sig08", ...CAT,
    title: "がんの多段階発がん", diff: 190, tags: ["多段階発がん", "大腸がん", "クローン進化"],
    explain: `<p>大腸がんは、正常上皮 → 腺腫 → がん という多段階の過程をたどり、
              その各段階で <i>APC</i>、<i>KRAS</i>、<i>TP53</i> などの変異が蓄積する。</p>
              <p>この「多段階発がん」の考え方から導かれることとして、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "1個の遺伝子の変異だけで、正常細胞は直ちにがん細胞になる。",
      "複数の変異が蓄積する必要があるため、発がんには長い時間がかかり、加齢とともに頻度が上がる。",
      "変異の順序は完全に決まっており、順序が違えばがんにならない。",
      "がんは環境要因だけで起こり、遺伝的変化は関与しない。",
    ],
    answer: 1,
    hints: [
      "何個もの変異が偶然そろう確率を考えましょう。時間がかかるでしょうか、すぐでしょうか。",
      "がんの発症率は年齢とともにどう変化するか思い出しましょう。",
    ],
    solution: `<p><b>【ステップ1】確率の問題として考える</b></p>
               <p>1個の細胞に特定の変異が起こる確率は非常に低い。
               それが<b>5〜8個も同じ細胞系列に蓄積する</b>には、
               長い時間と多数の細胞分裂が必要です。</p>
               <p>この予測は疫学とよく合います。がんの発症率は
               年齢のおよそ<b>4〜6乗</b>に比例して増加し、
               これは「4〜6個の律速段階がある」ことを示唆します。</p>
               <p><b>【ステップ2】フォーゲルシュタインのモデル</b></p>
               <table>
                 <tr><th>段階</th><th>主な変化</th></tr>
                 <tr><td>正常上皮 → 過形成</td><td><i>APC</i> の不活性化（Wnt経路の恒常活性化）</td></tr>
                 <tr><td>→ 早期腺腫</td><td>DNA低メチル化</td></tr>
                 <tr><td>→ 中期腺腫</td><td><i>KRAS</i> の活性化変異</td></tr>
                 <tr><td>→ 後期腺腫</td><td><i>SMAD4</i>（18q）の欠失</td></tr>
                 <tr><td>→ がん</td><td><i>TP53</i> の不活性化</td></tr>
                 <tr><td>→ 転移</td><td>さらなる変化</td></tr>
               </table>
               <p><b>【ステップ3】C が誤りである理由</b></p>
               <p>典型的な順序はありますが、<b>絶対ではありません</b>。
               重要なのは順序そのものより、<b>どの機能が失われたかの組み合わせ</b>です。
               ハナハンとワインバーグは、がんに共通して獲得される性質を
               <b>「がんの特徴（Hallmarks of Cancer）」</b>としてまとめました。</p>
               <ul>
                 <li>増殖シグナルの自給、増殖抑制シグナルの回避</li>
                 <li>細胞死の回避、複製の無限化</li>
                 <li>血管新生の誘導、浸潤・転移能力</li>
                 <li>代謝の再プログラム、免疫からの逃避</li>
               </ul>
               <p><b>【ステップ4】クローン進化</b></p>
               <p>がんの成り立ちは<b>体細胞レベルの自然選択</b>そのものです。
               変異でわずかに増殖が有利になった細胞が集団を占め、
               その中でさらに変異が起こる——この繰り返しです。
               治療への耐性が生じるのも、薬という選択圧の下で
               耐性クローンが選抜されるからにほかなりません。</p>
               <p>この理解が<b>早期発見（検診でのポリープ切除）</b>の根拠になります。
               すべての変異がそろう前に取り除けばよいのです。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "sig09", ...CAT,
    title: "誘導と決定を実験で区別する", diff: 285, tags: ["形成体", "移植実験", "発生"],
    explain: `<p>両生類の初期原腸胚の<b>原口背唇部</b>を、別の胚の腹側に移植すると、
              移植片の周囲の宿主細胞が本来とは異なる運命をとり、<b>二次胚（第二の体軸）</b>が形成される。</p>
              <p>この古典的実験（シュペーマン・マンゴールド）が示したこととして、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "移植片そのものが分裂して二次胚のすべての組織を作った。",
      "移植片が周囲の宿主細胞に働きかけ、その運命を変化させた（誘導）。",
      "宿主胚の細胞はすべて未分化のまま留まっていた。",
      "細胞の運命は受精の時点ですべて決まっている。",
    ],
    answer: 1,
    hints: [
      "二次胚を構成している細胞は、移植片由来でしょうか、それとも宿主由来でしょうか。",
      "この区別のために、彼らは色素の違う2種のイモリを使いました。",
    ],
    solution: `<p><b>【ステップ1】実験の巧妙な設計</b></p>
               <p>シュペーマンとマンゴールドは、<b>色素の異なる2種のイモリ</b>を使いました。
               ドナー（色が濃い）とホスト（色が薄い）を組み合わせることで、
               <b>できた二次胚のどの細胞がどちらに由来するか</b>を
               顕微鏡で直接見分けられるようにしたのです。</p>
               <p><b>【ステップ2】結果</b></p>
               <p>二次胚の<b>大部分は宿主（色が薄い）由来</b>でした。
               移植片自身は主に脊索になっただけです。</p>
               <p>つまり移植片は「自分で二次胚を作った」のではなく、
               <b>周囲の宿主細胞に働きかけて運命を変えさせた</b>。
               本来なら腹側の表皮になるはずだった細胞が、
               神経管や体節へと運命を変更したのです。
               これが<b>誘導（induction）</b>という概念の確立でした。</p>
               <p><b>【ステップ3】形成体（オーガナイザー）</b></p>
               <p>原口背唇部はこの働きから<b>形成体</b>と名づけられました。
               シュペーマンは1935年にノーベル賞を受賞しています
               （マンゴールドは実験の数年後に事故で亡くなり、受賞していません）。</p>
               <p><b>【ステップ4】分子的な実体</b></p>
               <p>形成体の正体が分かったのは60年後です。
               形成体は <b>ノギン、コーディン、フォリスタチン</b>を分泌しますが、
               これらは<b>BMPの阻害因子</b>でした。</p>
               <p>ここに逆説があります。神経を「作らせる」のではなく、
               <b>神経になることを妨げていた BMP シグナルを取り除いていた</b>のです。
               外胚葉の既定の運命は神経であり、BMP がそれを表皮へ向けている。
               形成体はその抑制を解除していたわけです
               （<b>デフォルトモデル</b>）。</p>
               <p><b>【ステップ5】用語の区別</b></p>
               <ul>
                 <li><b>決定（determination）</b>：運命が確定し、環境を変えても変わらない状態</li>
                 <li><b>分化（differentiation）</b>：実際に形態・機能が現れること</li>
                 <li><b>誘導（induction）</b>：ある細胞群が別の細胞群の運命に影響を与えること</li>
               </ul>
               <p>移植実験は、まさに「まだ決定していない細胞に誘導が働く」ことを示しました。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "sig10", ...CAT,
    title: "免疫チェックポイントの論理", diff: 310, tags: ["PD-1", "免疫チェックポイント", "がん免疫"],
    explain: `<p>抗PD-1抗体は、がん細胞を直接攻撃する薬ではないのに、
              一部の患者で長期の腫瘍縮小をもたらす。</p>
              <p>その作用機序として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "がん細胞表面の PD-1 に結合して、がん細胞を直接破壊する。",
      "T細胞の PD-1 とがん細胞側の PD-L1 の結合を遮断し、抑制されていたT細胞の攻撃を回復させる。",
      "T細胞の増殖を直接刺激して、数を増やす。",
      "がん細胞の DNA を損傷させ、アポトーシスを誘導する。",
    ],
    answer: 1,
    hints: [
      "PD-1 はT細胞側にある「ブレーキ」の受容体です。がん細胞はそのブレーキをどう利用しているでしょうか。",
      "薬はアクセルを踏むのではなく、ブレーキを外していると考えると理解しやすい。",
    ],
    solution: `<p><b>【ステップ1】発想の転換</b></p>
               <p>従来の抗がん剤は<b>がん細胞を直接叩く</b>ものでした。
               免疫チェックポイント阻害剤は標的が違います。
               狙うのは<b>患者自身の T細胞にかかっているブレーキ</b>です。</p>
               <p><b>【ステップ2】PD-1/PD-L1 の本来の役割</b></p>
               <p>PD-1 は活性化T細胞に発現する<b>抑制性受容体</b>で、
               本来は<b>自己免疫を防ぐ安全装置</b>です。
               正常組織が PD-L1 を出すことで
               「私は敵ではない」と T細胞に伝え、攻撃を止めさせます。</p>
               <p><b>【ステップ3】がん細胞による乗っ取り</b></p>
               <p>多くのがんは <b>PD-L1 を高発現</b>させ、
               この安全装置を悪用します。T細胞ががんを認識しても、
               PD-1 にブレーキがかかって攻撃できない——
               <b>免疫逃避</b>の状態です。</p>
               <p><b>【ステップ4】抗体の働き</b></p>
               <p>抗PD-1抗体は PD-1 と PD-L1 の結合を物理的に遮断します。
               ブレーキが外れたT細胞は、<b>もともと持っていた</b>
               がん認識能力を発揮して攻撃を再開します。</p>
               <p>この機序から重要な帰結が導かれます。</p>
               <table>
                 <tr><th>特徴</th><th>理由</th></tr>
                 <tr><td>効く患者と効かない患者の差が大きい</td><td>そもそも<b>がんを認識するT細胞がいる</b>ことが前提</td></tr>
                 <tr><td>変異の多いがん（腫瘍変異負荷が高い）でよく効く</td><td>変異が多い＝<b>ネオアンチゲン</b>が多い＝T細胞が認識しやすい</td></tr>
                 <tr><td>効いた場合は長期奏効しやすい</td><td>免疫は<b>記憶</b>を持つため</td></tr>
                 <tr><td>自己免疫性の副作用が出る</td><td>外したのは<b>自己免疫を防ぐ安全装置</b>そのもの</td></tr>
                 <tr><td>効果が出るまで時間がかかる</td><td>免疫応答の増幅に時間を要する</td></tr>
               </table>
               <p><b>【ステップ5】なぜ副作用が必然か</b></p>
               <p>免疫関連有害事象（大腸炎、甲状腺機能異常、肺臓炎など）は
               「予想外の副作用」ではありません。
               <b>自己への攻撃を抑える仕組みを意図的に外している</b>のだから、
               自己免疫的な反応が起こるのは機序から直接予測されることです。</p>
               <p class="tiny">本庶佑とジェームズ・アリソンが
               2018年のノーベル生理学・医学賞を受賞しました。
               アリソンは CTLA-4、本庶は PD-1 の発見と応用によるものです。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },
];
